import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { Pause, Play, Square, Volume2, X } from "lucide-react";

import {
  ArticleSpeechState,
  pauseArticleSpeech,
  resumeArticleSpeech,
  startArticleSpeech,
  stopArticleSpeech,
} from "../../lib/article-speech";

interface Word {
  start: number;
  end: number;
  text: string;
}

interface ArticleReaderModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  content?: string;
}

function tokenizeWords(text: string): Word[] {
  const words: Word[] = [];
  const regex = /\S+/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text))) {
    words.push({ start: match.index, end: match.index + match[0].length, text: match[0] });
  }
  return words;
}

// Words are sorted by start offset, so binary-search for the last word whose
// start is at or before the reported boundary charIndex.
function findWordIndex(words: Word[], charIndex: number): number {
  let lo = 0;
  let hi = words.length - 1;
  let result = -1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    if (words[mid].start <= charIndex) {
      result = mid;
      lo = mid + 1;
    } else {
      hi = mid - 1;
    }
  }
  return result;
}

// Content is fetched and validated by the caller (ArticleListenButton) before
// this modal ever opens — a fetch failure or bad status code shows a toast
// and never presents this popup, so this component only has to render and
// speak text it already knows is good.
export function ArticleReaderModal({ isOpen, onClose, title, content }: ArticleReaderModalProps) {
  const sessionId = useId();
  const [words, setWords] = useState<Word[]>([]);
  const [activeWordIndex, setActiveWordIndex] = useState(-1);
  const [playState, setPlayState] = useState<ArticleSpeechState>("idle");
  const mountedRef = useRef(true);
  const activeWordRef = useRef<HTMLSpanElement | null>(null);
  const hasStartedRef = useRef(false);
  const contentTextRef = useRef<string>("");

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleNotify = useCallback((state: ArticleSpeechState) => {
    if (!mountedRef.current) return;
    setPlayState(state);
  }, []);

  const handleBoundary = useCallback((charIndex: number, _charLength: number) => {
    if (!mountedRef.current) return;
    setWords((currentWords) => {
      const index = findWordIndex(currentWords, charIndex);
      setActiveWordIndex(index);
      return currentWords;
    });
  }, []);

  const beginReading = useCallback(
    (text: string) => {
      const normalizedText = startArticleSpeech(sessionId, text, handleNotify, handleBoundary);
      setWords(tokenizeWords(normalizedText));
      setActiveWordIndex(-1);
    },
    [sessionId, handleNotify, handleBoundary]
  );

  useEffect(() => {
    if (!isOpen) {
      hasStartedRef.current = false;
      return;
    }
    if (hasStartedRef.current || !content) return;
    hasStartedRef.current = true;
    contentTextRef.current = content;
    beginReading(content);
  }, [isOpen, content, beginReading]);

  useEffect(() => {
    if (activeWordRef.current) {
      activeWordRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [activeWordIndex]);

  const handleClose = useCallback(() => {
    stopArticleSpeech(sessionId);
    onClose();
  }, [sessionId, onClose]);

  // Stop speech if the modal is unmounted while still playing (e.g. route change).
  useEffect(() => {
    return () => {
      stopArticleSpeech(sessionId);
    };
  }, [sessionId]);

  const handleTogglePlayback = useCallback(() => {
    if (playState === "playing") {
      pauseArticleSpeech(sessionId);
    } else if (playState === "paused") {
      resumeArticleSpeech(sessionId);
    } else if (contentTextRef.current) {
      // Stopped or finished: Play restarts the article from the beginning.
      beginReading(contentTextRef.current);
    }
  }, [playState, sessionId, beginReading]);

  const handleStop = useCallback(() => {
    stopArticleSpeech(sessionId);
  }, [sessionId]);

  const wordSpans = useMemo(
    () =>
      words.map((word, index) => {
        const isActive = index === activeWordIndex;
        return (
          <span
            key={`${word.start}-${index}`}
            ref={isActive ? activeWordRef : undefined}
            className={
              isActive
                ? "bg-orange-400/90 text-slate-900 rounded px-0.5 -mx-0.5 shadow-sm scale-105 inline-block transition-all duration-150"
                : "transition-colors duration-150"
            }
          >
            {word.text}{" "}
          </span>
        );
      }),
    [words, activeWordIndex]
  );

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <React.Fragment>
          <motion.div
            key="article-reader-backdrop"
            className="fixed inset-0 z-[200] bg-slate-950/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={handleClose}
          />
          <motion.div
            key="article-reader-panel"
            role="dialog"
            aria-modal="true"
            aria-label={title || "Listen to article"}
            className="fixed inset-y-0 left-0 z-[201] flex h-full w-full flex-col bg-white shadow-2xl dark:bg-slate-900 sm:w-[540px] md:w-[640px]"
            initial={{ x: "-100%", opacity: 0.4 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-100%", opacity: 0.4 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
          >
            <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
              <div className="flex items-center gap-2 min-w-0">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-100 text-orange-600 dark:bg-orange-500/10 dark:text-orange-400">
                  <Volume2 className="h-5 w-5" />
                </div>
                <h2 className="truncate text-base font-semibold text-slate-900 dark:text-slate-100">
                  {title || "Listen to Article"}
                </h2>
              </div>
              <button
                type="button"
                onClick={handleClose}
                aria-label="Close"
                className="shrink-0 rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 py-5">
              <p className="text-lg leading-relaxed text-slate-700 dark:text-slate-200">{wordSpans}</p>
            </div>

            <div className="flex items-center justify-center gap-4 border-t border-slate-100 px-5 py-4 dark:border-slate-800">
              <button
                type="button"
                onClick={handleStop}
                aria-label="Stop"
                className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
              >
                <Square className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={handleTogglePlayback}
                aria-label={playState === "playing" ? "Pause" : "Resume"}
                className="flex h-14 w-14 items-center justify-center rounded-full bg-orange-500 text-white shadow-lg shadow-orange-500/30 transition-transform hover:scale-105 hover:bg-orange-600 active:scale-95"
              >
                {playState === "playing" ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
              </button>
              <div className="w-11" />
            </div>
          </motion.div>
        </React.Fragment>
      )}
    </AnimatePresence>,
    document.body
  );
}
